import { Link } from 'react-router-dom'

interface Feature {
  title: string
  description: string
  imagePlaceholder: string
  benefits: string[]
}

const features: Feature[] = [
  {
    title: 'AI-Powered Conversations',
    description: 'Chat naturally about your meals. Our AI understands context, preferences, and even your cravings.',
    imagePlaceholder: '💬🤖',
    benefits: [
      'Natural language meal planning',
      'Contextual recipe suggestions',
      'Adapts to your feedback',
    ],
  },
  {
    title: 'Visual Meal Calendar',
    description: 'Drag-and-drop your meals onto a beautiful calendar. Export to .ics for your favorite apps.',
    imagePlaceholder: '📅✨',
    benefits: [
      'Intuitive drag-and-drop interface',
      'Weekly and monthly views',
      'Export to Google Calendar, Apple Calendar',
    ],
  },
  {
    title: 'Seamless Grocery Shopping',
    description: 'One click sends your shopping list to Instacart. No manual entry, no hassle.',
    imagePlaceholder: '🛒🚀',
    benefits: [
      'Automatic ingredient aggregation',
      'Direct Instacart integration',
      'Smart quantity calculations',
    ],
  },
]

export function FeatureShowcase() {
  return (
    <section className="py-16 md:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-deep-charcoal mb-4">
            Features That Make Life Easier
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Everything you need for stress-free meal planning
          </p>
        </div>

        {/* Features */}
        <div className="space-y-16 md:space-y-24">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } gap-8 md:gap-12 items-center`}
            >
              {/* Image Placeholder */}
              <div className="w-full lg:w-1/2">
                <div className="bg-gradient-to-br from-butter-cream to-warm-cream rounded-2xl shadow-xl p-12 md:p-16 text-center border border-soft-tan">
                  <div className="text-8xl md:text-9xl mb-4">
                    {feature.imagePlaceholder}
                  </div>
                  <p className="text-sm md:text-base text-gray-500 italic">
                    Feature preview coming soon
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-1/2">
                <h3 className="text-2xl md:text-3xl font-bold text-deep-charcoal mb-4">
                  {feature.title}
                </h3>
                <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed">
                  {feature.description}
                </p>

                {/* Benefits List */}
                <ul className="space-y-3 mb-6">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li
                      key={benefitIndex}
                      className="flex items-start gap-3"
                    >
                      <span className="text-toast-gold text-xl flex-shrink-0 mt-0.5">
                        ✓
                      </span>
                      <span className="text-base md:text-lg text-gray-700">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Final CTA */}
        <div className="text-center mt-16 md:mt-20">
          <div className="bg-gradient-to-br from-toast-gold/10 to-transparent rounded-2xl p-8 md:p-12 border border-toast-gold/20">
            <h3 className="text-2xl md:text-3xl font-bold text-deep-charcoal mb-4">
              Ready to transform your meal planning?
            </h3>
            <p className="text-lg md:text-xl text-gray-600 mb-6 max-w-2xl mx-auto">
              Join the beta waitlist and be among the first to experience the future of meal planning
            </p>
            <Link
              to="/beta-signup"
              className="inline-block bg-toast-gold hover:bg-toast-gold-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Join Beta Waitlist
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}
